import { createContext, useContext } from 'react';

import dayjs from 'dayjs';

/**
 * Context for communicating which time-off segments have sticky labels active
 * (i.e. their label fits within the bar). When a segment's label overflows, its
 * sticky label is not rendered and the ScrollableText inside the block is shown
 * instead. Mirrors the assignment sticky-labels context.
 */
interface StickyTimeOffLabelsContextValue {
  /** Map of time-off ID -> Set of segment start dates (ISO strings) with sticky labels. */
  stickySegmentsByTimeOff: Map<number, Set<string>>;
}

const StickyTimeOffLabelsContext = createContext<StickyTimeOffLabelsContextValue>({
  stickySegmentsByTimeOff: new Map(),
});

export const StickyTimeOffLabelsProvider = StickyTimeOffLabelsContext.Provider;

/**
 * Returns true if the given time-off segment has an active sticky label (label
 * fits the bar). Checked by time-off ID and segment start date.
 */
export const useStickyTimeOffLabelActive = (
  timeOffId: number,
  segmentStartDate: dayjs.Dayjs
): boolean => {
  const { stickySegmentsByTimeOff } = useContext(StickyTimeOffLabelsContext);
  const segmentDates = stickySegmentsByTimeOff.get(timeOffId);
  if (!segmentDates) return false;
  return segmentDates.has(segmentStartDate.startOf('day').toISOString());
};
